import {
  Component,
  OnInit,
} from '@angular/core';
import { City, Model, Rate } from '../../../classes';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { WidgetsmanagerService } from '../../../services/widgetsmanager.service';
import { AlertController, NavParams, ModalController } from '@ionic/angular';
import { RateService } from 'src/app/services/rate.service';
import { HttpStatusCodesEnum } from 'src/app/utils/httpStatusCodesEnum';
import { RateValidators } from 'src/app/classes/validators/rate.validators';

@Component({
  selector: 'app-rate-detail',
  templateUrl: './rate-detail.component.html',
  styleUrls: ['./rate-detail.component.scss'],
})
export class RateDetailComponent implements OnInit {
  public rate: Rate;
  public isSpinnerVisible = false;
  public isEditing = false;
  public validation_messages: any;
  public rateForm: FormGroup;
  public models: Model[];
  public cities: City[];

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    public alertController: AlertController,
    private _rateSvc: RateService,
    private widgetsManager: WidgetsmanagerService,
    private modalController: ModalController,
    private navParams: NavParams
  ) {
  }

  ngOnInit(): void {
    const selectedRate = this.navParams.get('rate');
    this.models = this.navParams.get('models') || [];
    this.cities = this.navParams.get('cities') || [];
    if (selectedRate) {
      this.isEditing = true;
      this.rate = selectedRate;
    } else {
      this.rate = new Rate();
    }

    // Initialize form with custom validators for conditional dependencies
    this.rateForm = this.formBuilder.group({
      id: [this.rate.id || null],
      name: [this.rate.name || '', [Validators.required]],
      description: [this.rate.description || ''],

      // Basic fees (always required)
      activeFee: [this.rate.activeFee !== undefined ? this.rate.activeFee : null,
      [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      breakFee: [this.rate.breakFee !== undefined ? this.rate.breakFee : null,
      [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      unlockFee: [this.rate.unlockFee !== undefined ? this.rate.unlockFee : null,
      [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],
      bookingFee: [this.rate.bookingFee !== undefined ? this.rate.bookingFee : null,
      [Validators.required, Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],

      // Interval settings (always required)
      bookingIntervalMinutes: [this.rate.bookingIntervalMinutes !== undefined ? this.rate.bookingIntervalMinutes : null,
      [Validators.required, Validators.pattern('^[0-9]+$')]],
      activeIntervalMinutes: [this.rate.activeIntervalMinutes !== undefined ? this.rate.activeIntervalMinutes : null,
      [Validators.required, Validators.pattern('^[0-9]+$')]],
      breakIntervalMinutes: [this.rate.breakIntervalMinutes !== undefined ? this.rate.breakIntervalMinutes : null,
      [Validators.required, Validators.pattern('^[0-9]+$')]],

      // Threshold settings (if intervalThreshold is set → fee over threshold becomes required)
      activeIntervalThreshold: [this.rate.activeIntervalThreshold !== undefined ? this.rate.activeIntervalThreshold : null,
      [Validators.pattern('^[0-9]+$')]],
      breakIntervalThreshold: [this.rate.breakIntervalThreshold !== undefined ? this.rate.breakIntervalThreshold : null,
      [Validators.pattern('^[0-9]+$')]],
      bookingIntervalThreshold: [this.rate.bookingIntervalThreshold !== undefined ? this.rate.bookingIntervalThreshold : null,
      [Validators.pattern('^[0-9]+$')]],

      activeFeeOverThreshold: [this.rate.activeFeeOverThreshold !== undefined ? this.rate.activeFeeOverThreshold : null,
      [Validators.pattern('^[0-9]+(\\.[0-9]+)?$'), RateValidators.activeFeeOverThresholdRequired(), RateValidators.feeOverThresholdLogic('activeIntervalThreshold')]],
      breakFeeOverThreshold: [this.rate.breakFeeOverThreshold !== undefined ? this.rate.breakFeeOverThreshold : null,
      [Validators.pattern('^[0-9]+(\\.[0-9]+)?$'), RateValidators.breakFeeOverThresholdRequired(), RateValidators.feeOverThresholdLogic('breakIntervalThreshold')]],
      bookingFeeOverThreshold: [this.rate.bookingFeeOverThreshold !== undefined ? this.rate.bookingFeeOverThreshold : null,
      [Validators.pattern('^[0-9]+(\\.[0-9]+)?$'), RateValidators.bookingFeeOverThresholdRequired(), RateValidators.feeOverThresholdLogic('bookingIntervalThreshold')]],

      // Other optional settings
      dailySpendingCap: [this.rate.dailySpendingCap !== undefined ? this.rate.dailySpendingCap : null,
      [Validators.pattern('^[0-9]+(\\.[0-9]+)?$')]],

      // KM-related fields (kmFee ↔ kmInterval mutually required)
      kmFee: [this.rate.kmFee !== undefined ? this.rate.kmFee : null,
      [Validators.pattern('^[0-9]+(\\.[0-9]+)?$'), RateValidators.kmFeeRequired()]],
      kmInterval: [this.rate.kmInterval !== undefined ? this.rate.kmInterval : null,
      [Validators.pattern('^[0-9]+$'), RateValidators.kmIntervalRequired()]],
      kmIntervalThreshold: [this.rate.kmIntervalThreshold !== undefined ? this.rate.kmIntervalThreshold : null,
      [Validators.pattern('^[0-9]+$'), RateValidators.kmIntervalThresholdLogic()]],
      kmFeeOverThreshold: [this.rate.kmFeeOverThreshold !== undefined ? this.rate.kmFeeOverThreshold : null,
      [Validators.pattern('^[0-9]+(\\.[0-9]+)?$'), RateValidators.kmFeeOverThresholdRequired(), RateValidators.kmFeeOverThresholdLogic()]],

      // Relations
      modelId: [this.rate.modelId || null, [Validators.required]],
      cityId: [this.rate.cityId || null, [Validators.required, Validators.min(1)]],

      // Timeslots
      timeslots: [this.rate.timeslots !== undefined ? this.rate.timeslots : [], []]
    });

    // Add listeners to update validation when values change
    this.setupValidationListeners();

    this.validation_messages = {
      name: [
        { type: 'required', message: 'validation.required' }
      ],

      // Basic fees
      activeFee: [
        { type: 'required', message: 'validation.required' },
        { type: 'pattern', message: 'validation.number_format' },
      ],
      breakFee: [
        { type: 'required', message: 'validation.required' },
        { type: 'pattern', message: 'validation.number_format' },
      ],
      unlockFee: [
        { type: 'required', message: 'validation.required' },
        { type: 'pattern', message: 'validation.number_format' },
      ],
      bookingFee: [
        { type: 'required', message: 'validation.required' },
        { type: 'pattern', message: 'validation.number_format' },
      ],

      // Intervals
      bookingIntervalMinutes: [
        { type: 'required', message: 'validation.required' },
        { type: 'pattern', message: 'validation.integer_format' },
      ],
      activeIntervalMinutes: [
        { type: 'required', message: 'validation.required' },
        { type: 'pattern', message: 'validation.integer_format' },
      ],
      breakIntervalMinutes: [
        { type: 'required', message: 'validation.required' },
        { type: 'pattern', message: 'validation.integer_format' },
      ],

      // Threshold settings
      activeIntervalThreshold: [
        { type: 'pattern', message: 'validation.integer_format' },
      ],
      bookingIntervalThreshold: [
        { type: 'pattern', message: 'validation.integer_format' },
      ],
      breakIntervalThreshold: [
        { type: 'pattern', message: 'validation.integer_format' },
      ],
      activeFeeOverThreshold: [
        { type: 'pattern', message: 'validation.number_format' },
        { type: 'activeFeeOverThresholdRequired', message: 'validation.fee_over_threshold_required' },
        { type: 'feeOverThresholdWithoutBase', message: 'validation.fee_over_threshold_without_base' },
      ],
      breakFeeOverThreshold: [
        { type: 'pattern', message: 'validation.number_format' },
        { type: 'breakFeeOverThresholdRequired', message: 'validation.fee_over_threshold_required' },
        { type: 'feeOverThresholdWithoutBase', message: 'validation.fee_over_threshold_without_base' },
      ],
      bookingFeeOverThreshold: [
        { type: 'pattern', message: 'validation.number_format' },
        { type: 'bookingFeeOverThresholdRequired', message: 'validation.fee_over_threshold_required' },
        { type: 'feeOverThresholdWithoutBase', message: 'validation.fee_over_threshold_without_base' },
      ],

      // Other optional fields
      dailySpendingCap: [
        { type: 'pattern', message: 'validation.number_format' },
      ],

      // KM fields
      kmFee: [
        { type: 'pattern', message: 'validation.number_format' },
        { type: 'kmFeeRequired', message: 'validation.km_fee_required' },
      ],
      kmInterval: [
        { type: 'pattern', message: 'validation.integer_format' },
        { type: 'kmIntervalRequired', message: 'validation.km_interval_required' },
      ],
      kmIntervalThreshold: [
        { type: 'pattern', message: 'validation.integer_format' },
        { type: 'kmIntervalThresholdWithoutBase', message: 'validation.km_threshold_without_base' },
      ],
      kmFeeOverThreshold: [
        { type: 'pattern', message: 'validation.number_format' },
        { type: 'kmFeeOverThresholdRequired', message: 'validation.km_fee_over_threshold_required' },
        { type: 'kmFeeOverThresholdWithoutBase', message: 'validation.km_fee_over_threshold_without_base' },
      ],

      // Relations
      modelId: [
        { type: 'required', message: 'validation.required' },
      ],
      cityId: [
        { type: 'required', message: 'validation.required' },
      ],
    };

    this.rateForm.get('activeIntervalThreshold')
      .valueChanges.subscribe(() => {
        this.rateForm.get('activeFeeOverThreshold')
          .updateValueAndValidity({ onlySelf: true });
      });

    this.rateForm.get('breakIntervalThreshold')
      .valueChanges.subscribe(() => {
        this.rateForm.get('breakFeeOverThreshold')
          .updateValueAndValidity({ onlySelf: true });
      });

    this.rateForm.get('bookingIntervalThreshold')
      .valueChanges.subscribe(() => {
        this.rateForm.get('bookingFeeOverThreshold')
          .updateValueAndValidity({ onlySelf: true });
      });
  }

  // Setup listeners to update validation when values change
  private setupValidationListeners(): void {
    // Get control references once
    const kmFeeControl = this.rateForm.get('kmFee');
    const kmIntervalControl = this.rateForm.get('kmInterval');
    const kmIntervalThresholdControl = this.rateForm.get('kmIntervalThreshold');
    const kmFeeOverThresholdControl = this.rateForm.get('kmFeeOverThreshold');
    const activeIntervalThresholdControl = this.rateForm.get('activeIntervalThreshold');
    const bookingIntervalThresholdControl = this.rateForm.get('bookingIntervalThreshold');
    const breakIntervalThresholdControl = this.rateForm.get('breakIntervalThreshold');
    const activeFeeOverThresholdControl = this.rateForm.get('activeFeeOverThreshold');
    const breakFeeOverThresholdControl = this.rateForm.get('breakFeeOverThreshold');
    const bookingFeeOverThresholdControl = this.rateForm.get('bookingFeeOverThreshold');

    // When kmFee changes, revalidate kmInterval and threshold fields
    if (kmFeeControl) {
      kmFeeControl.valueChanges.subscribe(() => {
        // Revalidate kmInterval always (even when cleared)
        if (kmIntervalControl) {
          kmIntervalControl.updateValueAndValidity({ emitEvent: false });
        }
        // Revalidate kmIntervalThreshold
        if (kmIntervalThresholdControl) {
          kmIntervalThresholdControl.updateValueAndValidity({ emitEvent: false });
        }
        // If kmFee is cleared, also clear kmIntervalThreshold and kmFeeOverThreshold
        const kmFeeValue = kmFeeControl.value;
        if ((kmFeeValue == null || kmFeeValue === '') && kmIntervalThresholdControl) {
          kmIntervalThresholdControl.setValue(null);
          if (kmFeeOverThresholdControl) {
            kmFeeOverThresholdControl.setValue(null);
          }
        }
      });
    }

    // When kmInterval changes, revalidate kmFee and threshold fields
    if (kmIntervalControl) {
      kmIntervalControl.valueChanges.subscribe(() => {
        // Revalidate kmFee always (even when cleared)
        if (kmFeeControl) {
          kmFeeControl.updateValueAndValidity({ emitEvent: false });
        }
        // Revalidate kmIntervalThreshold
        if (kmIntervalThresholdControl) {
          kmIntervalThresholdControl.updateValueAndValidity({ emitEvent: false });
        }
        // If kmInterval is cleared, also clear kmIntervalThreshold and kmFeeOverThreshold
        const kmIntervalValue = kmIntervalControl.value;
        if ((kmIntervalValue == null || kmIntervalValue === '') && kmIntervalThresholdControl) {
          kmIntervalThresholdControl.setValue(null);
          if (kmFeeOverThresholdControl) {
            kmFeeOverThresholdControl.setValue(null);
          }
        }
      });
    }

    // When kmIntervalThreshold changes, revalidate kmFeeOverThreshold
    if (kmIntervalThresholdControl) {
      kmIntervalThresholdControl.valueChanges.subscribe(() => {
        if (kmFeeOverThresholdControl) {
          kmFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
        }
        // If kmIntervalThreshold is cleared, also clear kmFeeOverThreshold
        const kmIntervalThresholdValue = kmIntervalThresholdControl.value;
        if ((kmIntervalThresholdValue == null || kmIntervalThresholdValue === '') && kmFeeOverThresholdControl) {
          kmFeeOverThresholdControl.setValue(null);
        }
      });
    }

    // When intervalThreshold changes, revalidate relative fee over threshold fields
    if (activeIntervalThresholdControl) {
      activeIntervalThresholdControl.valueChanges.subscribe(() => {
        if (activeFeeOverThresholdControl) {
          activeFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
        }
        // If intervalThreshold is cleared, also clear all fee over threshold fields
        const activeIntervalThresholdValue = activeIntervalThresholdControl.value;
        if (activeIntervalThresholdValue == null || activeIntervalThresholdValue === '') {
          if (activeFeeOverThresholdControl) {
            activeFeeOverThresholdControl.setValue(null);
          }
        }
      });
    }
    if (breakFeeOverThresholdControl) {
      breakFeeOverThresholdControl.valueChanges.subscribe(() => {
        if (breakFeeOverThresholdControl) {
          breakFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
        }
        // If intervalThreshold is cleared, also clear relative fee over threshold fields
        const breakIntervalThresholdValue = breakIntervalThresholdControl.value;
        if (breakIntervalThresholdValue == null || breakIntervalThresholdValue === '') {
          if (breakFeeOverThresholdControl) {
            breakFeeOverThresholdControl.setValue(null);
          }
        }
      });
    }
    if (bookingFeeOverThresholdControl) {
      bookingFeeOverThresholdControl.valueChanges.subscribe(() => {
        if (bookingFeeOverThresholdControl) {
          bookingFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
        }
        // If intervalThreshold is cleared, also clear relative fee over threshold fields
        const bookingIntervalThresholdValue = bookingIntervalThresholdControl.value;
        if (bookingIntervalThresholdValue == null || bookingIntervalThresholdValue === '') {
          if (bookingFeeOverThresholdControl) {
            bookingFeeOverThresholdControl.setValue(null);
          }
        }
      });
    }

    // When base fees change, revalidate fee over threshold fields for logic validator
    const activeFeeControl = this.rateForm.get('activeFee');
    const breakFeeControl = this.rateForm.get('breakFee');
    const bookingFeeControl = this.rateForm.get('bookingFee');

    if (activeFeeControl && activeFeeOverThresholdControl) {
      activeFeeControl.valueChanges.subscribe(() => {
        activeFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
      });
    }

    if (breakFeeControl && breakFeeOverThresholdControl) {
      breakFeeControl.valueChanges.subscribe(() => {
        breakFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
      });
    }

    if (bookingFeeControl && bookingFeeOverThresholdControl) {
      bookingFeeControl.valueChanges.subscribe(() => {
        bookingFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
      });
    }

    // When base kmFee changes, revalidate kmFeeOverThreshold for logic validator
    if (kmFeeControl && kmFeeOverThresholdControl) {
      kmFeeControl.valueChanges.subscribe(() => {
        kmFeeOverThresholdControl.updateValueAndValidity({ emitEvent: false });
      });
    }
  }

  public async closePopover(reloadParent = false): Promise<void> {
    await this.modalController.dismiss(reloadParent);
  }

  async onSubmit(): Promise<void> {
    // Validate form
    if (!this.rateForm.valid) {
      this.widgetsManager.showToastMessage('danger',
        this.translate.instant('rate_detail.form_invalid'));
      return;
    }

    this.isSpinnerVisible = true;

    if (this.isEditing) {
      this._rateSvc.update(this.rate.id, this.rateForm.value).subscribe(
        () => {
          console.log('@@@ onSubmit update OK @@@');
          this.isSpinnerVisible = false;
          const actiondMsg = this.translate.instant('rate_detail.update_rate_ok');
          this.widgetsManager.showToastMessage('success', actiondMsg);
          this.closePopover(true);
        },
        (err) => {
          this.isSpinnerVisible = false;
          let actiondMsg = this.translate.instant('rate_detail.update_rate_error');
          if (err.status === HttpStatusCodesEnum.CONFLICT && err.error && err.error.overlappingTimeslot) {
            actiondMsg = this.translate.instant('overlappingTimeslot');
          } else if (err.status === HttpStatusCodesEnum.CONFLICT) {
            actiondMsg = this.translate.instant('rate_detail.rate_conflict_error');
          }
          this.widgetsManager.showToastMessage('danger', actiondMsg);
        }
      );
    } else {
      this._rateSvc.create(this.rateForm.value).subscribe(
        () => {
          console.log('@@@ onSubmit create OK @@@');
          this.isSpinnerVisible = false;
          const actiondMsg = this.translate.instant('rate_detail.create_rate_ok');
          this.widgetsManager.showToastMessage('success', actiondMsg);
          this.closePopover(true);
        },
        (err) => {
          this.isSpinnerVisible = false;
          let actiondMsg = this.translate.instant('rate_detail.create_rate_error');
          if (err.status === HttpStatusCodesEnum.CONFLICT && err.error && err.error.overlappingTimeslot) {
            actiondMsg = this.translate.instant('overlappingTimeslot');
          } else if (err.status === HttpStatusCodesEnum.CONFLICT) {
            actiondMsg = this.translate.instant('rate_detail.rate_conflict_error');
          }
          this.widgetsManager.showToastMessage('danger', actiondMsg);
        }
      );
    }
  }

  async deleteRate(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant('Are you sure to proceed?'),
      buttons: [
        {
          text: this.translate.instant('Cancel'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: 'Ok',
          handler: () => {
            this._rateSvc.delete(this.rate.id).subscribe(
              () => {
                const msgOK = this.translate.instant('rate_detail.delete_rate_ok');
                this.widgetsManager.showToastMessage('success', msgOK);
                this.closePopover(true);
              },
              (err) => {
                console.log('ERR: ', err);
                const msgKO = this.translate.instant('rate_detail.delete_rate_error');
                this.widgetsManager.showToastMessage('danger', msgKO);
              }
            );
          },
        },
      ],
    });

    await alert.present();
  }

  saveTimeslots(timeslots) {
    this.rateForm.controls['timeslots'].setValue(timeslots);
  }
}